import React, { useState, useEffect } from 'react';
import { X, DollarSign, AlertCircle, CheckCircle, Receipt, ArrowLeft, BanknoteIcon, Building2, Smartphone } from 'lucide-react';
import api from '../services/api';
import Modal from './Common/Modal';

const DepositRefundModal = ({ isOpen, onClose, lease, onRefundComplete }) => {
    const [leaseDetails, setLeaseDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [step, setStep] = useState('review'); // 'review' | 'deductions' | 'confirm' | 'success'
    const [result, setResult] = useState(null);
    const [showLeaseTerminate, setShowLeaseTerminate] = useState(false);
    const [terminating, setTerminating] = useState(false);

    const [formData, setFormData] = useState({
        deductions: 0,
        deduction_reason: '',
        refund_method: 'cash',
        mpesa_receipt_number: '',
        also_terminate: false,
        termination_reason: ''
    });

    const depositAmount = parseFloat(leaseDetails?.deposit_amount || lease?.deposit_amount || 0);
    const deductions = parseFloat(formData.deductions) || 0;
    const netRefund = Math.max(0, depositAmount - deductions);

    useEffect(() => {
        if (isOpen && (lease?.id || leaseDetails?.id)) {
            fetchLeaseDetails();
        } else if (isOpen) {
            setLoading(false);
        }
    }, [isOpen, lease]);

    const fetchLeaseDetails = async () => {
        try {
            setLoading(true);
            const leaseId = lease?.id || leaseDetails?.id;
            if (!leaseId) {
                setLoading(false);
                return;
            }
            const response = await api.get(`/leases/${leaseId}`);
            setLeaseDetails(response.data.data);
        } catch (error) {
            console.error('Error fetching lease details:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeductionChange = (value) => {
        const ded = parseFloat(value) || 0;
        if (ded < 0) return;
        if (ded > depositAmount) {
            setFormData({ ...formData, deductions: depositAmount });
            return;
        }
        setFormData({ ...formData, deductions: ded });
    };

    const handleSubmitRefund = async () => {
        setSubmitting(true);
        try {
            const leaseId = lease?.id || leaseDetails?.id;
            
            // 1. Process the deposit refund
            const refundResponse = await api.post(`/leases/${leaseId}/deposit-refund`, {
                refund_amount: netRefund,
                deductions: deductions,
                deduction_reason: formData.deduction_reason || (deductions > 0 ? 'Damages/repairs' : ''),
                refund_method: formData.refund_method,
                mpesa_receipt_number: formData.mpesa_receipt_number || null
            });

            // 2. Optionally terminate the lease
            if (formData.also_terminate && formData.termination_reason) {
                setTerminating(true);
                await api.post(`/leases/${leaseId}/terminate`, {
                    reason: formData.termination_reason
                });
                setTerminating(false);
            }

            setResult(refundResponse.data.data);
            setStep('success');
            
            if (onRefundComplete) onRefundComplete();
        } catch (error) {
            console.error('Error processing deposit refund:', error);
            alert('Failed to process deposit refund: ' + (error.response?.data?.error || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    // Check if deposit was already refunded
    const leaseData = leaseDetails || lease;
    const isAlreadyRefunded = leaseData?.deposit_refunded > 0 || 
        (leaseData?.metadata?.deposit_refunded === true);

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={onClose} 
            title={
                step === 'success' 
                    ? '✅ Deposit Refunded Successfully' 
                    : `Security Deposit Refund - ${leaseData?.asset_name || 'Property'} Unit ${leaseData?.unit_number || ''}`
            } 
            size="md"
        >
            {loading ? (
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-2 text-gray-500">Loading lease details...</p>
                </div>
            ) : isAlreadyRefunded ? (
                <div className="text-center py-8">
                    <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Deposit Already Refunded</h3>
                    <p className="text-gray-500 mb-4">
                        The security deposit for this lease has already been refunded.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg inline-block text-left">
                        <p className="text-sm text-gray-600">
                            <strong>Original Deposit:</strong> KES {parseFloat(leaseData.deposit_amount || 0).toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-600">
                            <strong>Amount Refunded:</strong> KES {parseFloat(leaseData.deposit_refunded || 0).toLocaleString()}
                        </p>
                        {leaseData.deposit_deductions > 0 && (
                            <p className="text-sm text-gray-600">
                                <strong>Deductions:</strong> KES {parseFloat(leaseData.deposit_deductions).toLocaleString()}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                    >
                        Close
                    </button>
                </div>
            ) : depositAmount <= 0 ? (
                <div className="text-center py-8">
                    <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Security Deposit Found</h3>
                    <p className="text-gray-500 mb-4">
                        This lease does not have a security deposit recorded. Deposit amount: KES 0
                    </p>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                    >
                        Close
                    </button>
                </div>
            ) : step === 'success' ? (
                <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 p-4 rounded-lg text-center">
                        <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-2" />
                        <h3 className="text-lg font-semibold text-green-800 mb-1">Deposit Refund Processed!</h3>
                        <p className="text-green-600">{result?.message}</p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                        <h4 className="font-semibold text-gray-900">Refund Summary</h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                                <p className="text-gray-500">Original Deposit</p>
                                <p className="font-medium">KES {(result?.original_deposit || depositAmount).toLocaleString()}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Deductions</p>
                                <p className="font-medium text-red-600">- KES {(result?.deductions || deductions).toLocaleString()}</p>
                            </div>
                            <div className="col-span-2 border-t pt-2">
                                <p className="text-gray-500">Net Refund</p>
                                <p className="text-lg font-bold text-green-600">KES {(result?.net_refund || netRefund).toLocaleString()}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Refund Method</p>
                                <p className="font-medium capitalize">{result?.refund_method || formData.refund_method}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Refund ID</p>
                                <p className="font-medium text-indigo-600">#{result?.refund_id}</p>
                            </div>
                        </div>
                    </div>

                    {formData.also_terminate && (
                        <div className="bg-blue-50 p-3 rounded-lg">
                            <p className="text-sm text-blue-700">✓ Lease has also been terminated</p>
                        </div>
                    )}

                    <div className="flex justify-end">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                        >
                            Done
                        </button>
                    </div>
                </div>
            ) : step === 'review' ? (
                <div className="space-y-6">
                    {/* Deposit Summary */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-3">Deposit Summary</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-gray-500">Security Deposit</p>
                                <p className="text-xl font-bold text-indigo-600">KES {depositAmount.toLocaleString()}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Tenant</p>
                                <p className="text-sm font-medium">{leaseData?.first_name} {leaseData?.last_name}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Property</p>
                                <p className="text-sm">{leaseData?.asset_name} - Unit {leaseData?.unit_number}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Lease Period</p>
                                <p className="text-sm">
                                    {leaseData?.start_date ? new Date(leaseData.start_date).toLocaleDateString() : 'N/A'} - {leaseData?.end_date ? new Date(leaseData.end_date).toLocaleDateString() : 'N/A'}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="border-t pt-4">
                        <button
                            onClick={() => setStep('deductions')}
                            className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium flex items-center justify-center"
                        >
                            <DollarSign className="h-5 w-5 mr-2" />
                            Process Deposit Refund
                        </button>
                    </div>
                </div>
            ) : step === 'deductions' ? (
                <div className="space-y-6">
                    <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
                        <div className="flex items-start">
                            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                            <p className="text-sm text-yellow-800">
                                If there are any damages or outstanding bills, you can deduct from the deposit. 
                                The remaining amount will be refunded to the tenant.
                            </p>
                        </div>
                    </div>

                    {/* Deductions */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Deductions for Damages / Repairs (KES)
                        </label>
                        <input
                            type="number"
                            value={formData.deductions}
                            onChange={(e) => handleDeductionChange(e.target.value)}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder="0"
                            min="0"
                            max={depositAmount}
                        />
                        <p className="text-xs text-gray-500 mt-1">Max: KES {depositAmount.toLocaleString()}</p>
                    </div>

                    {parseFloat(formData.deductions) > 0 && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Reason for Deductions *
                            </label>
                            <textarea
                                value={formData.deduction_reason}
                                onChange={(e) => setFormData({ ...formData, deduction_reason: e.target.value })}
                                rows={3}
                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                placeholder="e.g., Repairs to damaged wall, unpaid utility bills..."
                                required
                            />
                        </div>
                    )}

                    {/* Refund Method */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Refund Method *
                        </label>
                        <select
                            value={formData.refund_method}
                            onChange={(e) => setFormData({ ...formData, refund_method: e.target.value })}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        >
                            <option value="cash">Cash</option>
                            <option value="bank_transfer">Bank Transfer</option>
                            <option value="mpesa">M-Pesa</option>
                            <option value="cheque">Cheque</option>
                        </select>
                    </div>

                    {formData.refund_method === 'mpesa' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                M-Pesa Receipt Number (Transaction ID)
                            </label>
                            <input
                                type="text"
                                value={formData.mpesa_receipt_number}
                                onChange={(e) => setFormData({ ...formData, mpesa_receipt_number: e.target.value })}
                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                placeholder="e.g., QQQ1234567"
                            />
                        </div>
                    )}

                    {/* Live calculation */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Refund Calculation</h4>
                        <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                                <span>Security Deposit</span>
                                <span className="font-medium">KES {depositAmount.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-red-600">
                                <span>Deductions</span>
                                <span className="font-medium">- KES {deductions.toLocaleString()}</span>
                            </div>
                            <div className="border-t pt-1 flex justify-between text-base font-bold">
                                <span>Net Refund</span>
                                <span className="text-green-600">KES {netRefund.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>

                    {/* Also terminate lease option */}
                    <div className="border-t pt-4">
                        <label className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                checked={formData.also_terminate}
                                onChange={(e) => setFormData({ ...formData, also_terminate: e.target.checked })}
                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-sm font-medium text-gray-700">Also terminate this lease</span>
                        </label>
                        {formData.also_terminate && (
                            <div className="mt-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Termination Reason *
                                </label>
                                <textarea
                                    value={formData.termination_reason}
                                    onChange={(e) => setFormData({ ...formData, termination_reason: e.target.value })}
                                    rows={2}
                                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                    placeholder="e.g., Tenant moving out, Lease ended..."
                                    required
                                />
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-between space-x-3 pt-2">
                        <button
                            onClick={() => setStep('review')}
                            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                            <ArrowLeft className="h-4 w-4 mr-1" />
                            Back
                        </button>
                        <button
                            onClick={() => setStep('confirm')}
                            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                        >
                            Continue to Confirm
                        </button>
                    </div>
                </div>
            ) : step === 'confirm' ? (
                <div className="space-y-6">
                    <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                        <div className="flex items-start">
                            <AlertCircle className="h-6 w-6 text-orange-600 mt-0.5 mr-3 flex-shrink-0" />
                            <div>
                                <h4 className="font-semibold text-orange-800 mb-1">Confirm Deposit Refund</h4>
                                <p className="text-sm text-orange-700">
                                    Please review the details below carefully. This action will create a refund record 
                                    and mark the deposit as refunded.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                        <div className="flex justify-between">
                            <span className="text-gray-600">Tenant:</span>
                            <span className="font-medium">{leaseData?.first_name} {leaseData?.last_name}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Property:</span>
                            <span className="font-medium">{leaseData?.asset_name} - Unit {leaseData?.unit_number}</span>
                        </div>
                        <div className="border-t pt-3">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Original Deposit:</span>
                                <span className="font-medium">KES {depositAmount.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-red-600">
                                <span>Deductions:</span>
                                <span className="font-medium">- KES {deductions.toLocaleString()}</span>
                            </div>
                            {deductions > 0 && (
                                <div className="text-xs text-gray-500 mt-1 ml-4">
                                    Reason: {formData.deduction_reason}
                                </div>
                            )}
                            <div className="border-t pt-2 flex justify-between text-lg font-bold">
                                <span>Net Refund:</span>
                                <span className="text-green-600">KES {netRefund.toLocaleString()}</span>
                            </div>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Refund Method:</span>
                            <span className="font-medium capitalize">{formData.refund_method}</span>
                        </div>
                        {formData.refund_method === 'mpesa' && formData.mpesa_receipt_number && (
                            <div className="flex justify-between">
                                <span className="text-gray-600">M-Pesa Receipt:</span>
                                <span className="font-medium">{formData.mpesa_receipt_number}</span>
                            </div>
                        )}
                        {formData.also_terminate && (
                            <div className="bg-blue-50 p-2 rounded text-sm text-blue-700">
                                ⚠️ Lease will also be terminated
                                {formData.termination_reason && <div className="text-xs mt-1">Reason: {formData.termination_reason}</div>}
                            </div>
                        )}
                    </div>

                    <div className="flex justify-between space-x-3">
                        <button
                            onClick={() => setStep('deductions')}
                            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            disabled={submitting}
                        >
                            <ArrowLeft className="h-4 w-4 mr-1" />
                            Edit
                        </button>
                        <button
                            onClick={handleSubmitRefund}
                            disabled={submitting || terminating}
                            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
                        >
                            {submitting || terminating ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    {terminating ? 'Terminating Lease...' : 'Processing Refund...'}
                                </>
                            ) : (
                                <>
                                    <Receipt className="h-4 w-4 mr-2" />
                                    Confirm & Process Refund
                                </>
                            )}
                        </button>
                    </div>
                </div>
            ) : null}
        </Modal>
    );
};

export default DepositRefundModal;
